import { useEffect, useState } from 'react';

import { fetchJSON } from '../api';
import WorkflowBar from '../components/WorkflowBar';
import { useProject } from '../context/ProjectContext';

/**
 * Project Overview — physical-state framing (PR #194, 2026-05-13).
 *
 * Replaces the previous 4-stage cascade + duplicate % cards. Every FAR row is
 * shown in exactly one physical state (Tagged / Untaggable / Retired / Missing
 * / Pending), so the counts ALWAYS sum to the universe — no double-counting,
 * no broken percentages. PM-facing copy is in physical-world terms (Tagged,
 * Missing, Retired) instead of process-step jargon (Collected, Matched,
 * Reconciled). Backend endpoint /overview-stats was extended to return a
 * `physical` block alongside the legacy fields; this page consumes only that
 * + by_building.
 */
function derivePhysical(data) {
  // Pull the new physical block. Fall back to deriving from legacy fields if
  // the backend hasn't rolled yet — keeps the page working during deploy gaps.
  return (
    data.physical || {
      tagged: data.collection?.collected || 0,
      moved: data.collection?.relocated || 0,
      untaggable: data.not_taggable || 0,
      retired: data.reconciliation?.confirmed_retirement || 0,
      missing: data.reconciliation?.not_found || 0,
      pending: 0,
      new_finds: data.reconciliation?.discoveries || 0,
      universe: (data.total_register || 0) + (data.not_taggable || 0),
    }
  );
}

export default function ProjectOverview() {
  const { selected } = useProject();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [buildingSort, setBuildingSort] = useState('remaining');

  useEffect(() => {
    if (!selected) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const j = await fetchJSON(`/${selected.id}/overview-stats`);
        if (!cancelled) {
          setData(j);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected]);

  if (error) return <div className="py-8 text-red-600">Error: {error}</div>;
  if (!data) return <Skeleton />;

  const phys = derivePhysical(data);
  const universe = phys.universe || 1;

  // Verification = team has put hands on every item (any terminal physical state).
  const verified = phys.tagged + phys.untaggable + phys.retired + phys.missing;
  const verifiedPct = Math.round((verified / universe) * 100);

  // Reconciliation math — must match Reconciliation.jsx exactly so the
  // stage card and the page agree (PR #195). The page treats NT, moved,
  // and confirmed retirement as RESOLVED (PR #146/#149/#161); only
  // missing + unconfirmed discoveries + recheck + conflicted remain open.
  const matchedCount = phys.tagged + phys.untaggable; // tagged ∪ NT
  const reconciledCount = matchedCount + phys.retired;
  const reconciledPct = Math.round((reconciledCount / universe) * 100);

  const confirmedAdds = data.reconciliation?.confirmed_additions || 0;
  const unconfirmedDiscoveries = Math.max(0, phys.new_finds - confirmedAdds);
  const recheckCount = data.reconciliation?.recheck_required || 0;
  const conflictedCount = data.reconciliation?.conflicted || 0;
  const openDiscrepancyCount =
    phys.missing + unconfirmedDiscoveries + recheckCount + conflictedCount;

  const discrepancies = {
    missing: phys.missing,
    new_finds: unconfirmedDiscoveries,
    recheck: recheckCount,
    conflicted: conflictedCount,
    total: openDiscrepancyCount,
  };

  // Stage progression — denominators chosen so each stage card matches its
  // dedicated page (no surprises when the PM clicks through).
  const stages = [
    {
      key: 'collection',
      label: 'Collection',
      done: verified,
      total: universe,
      color: 'indigo',
      caption: 'team investigated each FAR row',
    },
    {
      key: 'qa',
      label: 'QA Review',
      done: data.qa?.approved || 0,
      total: phys.tagged || 0,
      color: 'purple',
      caption: 'AI extractions reviewed',
    },
    {
      key: 'reconciliation',
      label: 'Reconciliation',
      done: reconciledCount,
      total: universe,
      color: 'blue',
      caption: 'matched + NT + retired (matches Reconciliation page)',
    },
    {
      key: 'delivery',
      label: 'Final Delivery',
      done: data.stage === 'final_delivery' ? 1 : 0,
      total: 1,
      color: 'emerald',
      caption: 'workbook shipped & signed',
    },
  ];

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900">Project Overview</h2>
      <p className="mb-6 text-sm text-gray-400">
        {selected.name}
        {selected.client_name ? ` — ${selected.client_name}` : ''}
      </p>

      <WorkflowBar visibleAt="collection" />

      {/* ── PM headline — two numbers, both matching their pages ─── */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <p className="text-xs font-semibold tracking-wider text-gray-500 uppercase">
            Physical verification
          </p>
          <p className="mt-2 text-3xl font-bold text-emerald-600">{verifiedPct}%</p>
          <p className="mt-1 text-sm text-gray-500">
            {verified.toLocaleString()} of {universe.toLocaleString()} FAR items investigated — team
            work
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <p className="text-xs font-semibold tracking-wider text-gray-500 uppercase">
            Reconciliation
          </p>
          <p className="mt-2 text-3xl font-bold text-blue-600">{reconciledPct}%</p>
          <p className="mt-1 text-sm text-gray-500">
            {reconciledCount.toLocaleString()} of {universe.toLocaleString()} reconciled —{' '}
            <span className="font-semibold text-amber-600">
              {openDiscrepancyCount.toLocaleString()} still need a decision
            </span>
          </p>
        </div>
      </div>

      {/* ── Production-line stages ──────────────────────────────── */}
      <StagePipeline stages={stages} currentStage={data.stage} />

      {/* ── Accountability stripe — every FAR row in exactly one state ─ */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5">
        <p className="mb-3 text-sm font-semibold tracking-wider text-gray-500 uppercase">
          Physical states ({universe.toLocaleString()} FAR items on the books)
        </p>
        <Stripe phys={phys} universe={universe} />
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
          <StateChip
            label="Tagged"
            count={phys.tagged}
            color="emerald"
            sub={phys.moved > 0 ? `${phys.moved.toLocaleString()} moved` : null}
          />
          <StateChip label="Untaggable" count={phys.untaggable} color="purple" />
          <StateChip label="Retired" count={phys.retired} color="orange" />
          <StateChip label="Missing" count={phys.missing} color="red" />
          <StateChip
            label="Pending"
            count={phys.pending}
            color={phys.pending === 0 ? 'gray' : 'amber'}
          />
        </div>
      </div>

      {/* ── Open discrepancies — what's left to discuss with client ── */}
      {discrepancies.total > 0 && <Discrepancies d={discrepancies} />}

      {/* ── Deliverable readiness — gates as a checklist ───────── */}
      <DeliverableReadiness phys={phys} stage={data.stage} />

      {/* ── Building progress (kept — actionable) ──────────────── */}
      <BuildingProgress
        buildings={data.by_building || []}
        sort={buildingSort}
        onSort={setBuildingSort}
      />
    </div>
  );
}

/* ── Stage pipeline: 4 stages with honest per-stage denominators ── */
function StagePipeline({ stages, currentStage }) {
  return (
    <div className="mb-6 grid grid-cols-1 gap-2 md:grid-cols-4">
      {stages.map((s, i) => (
        <StageCard
          key={s.key}
          stage={s}
          isCurrent={currentStage === s.key}
          isLast={i === stages.length - 1}
        />
      ))}
    </div>
  );
}

function StageCard({ stage, isCurrent }) {
  const pct = stage.total > 0 ? Math.round((stage.done / stage.total) * 100) : 0;
  const palette = {
    indigo: {
      bg: 'bg-indigo-50',
      border: 'border-indigo-200',
      text: 'text-indigo-700',
      bar: 'bg-indigo-500',
      barBg: 'bg-indigo-100',
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      text: 'text-purple-700',
      bar: 'bg-purple-500',
      barBg: 'bg-purple-100',
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      bar: 'bg-blue-500',
      barBg: 'bg-blue-100',
    },
    emerald: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-700',
      bar: 'bg-emerald-500',
      barBg: 'bg-emerald-100',
    },
  };
  const c = palette[stage.color];
  const status = pct >= 100 ? '✓ done' : pct > 0 ? '→ in progress' : '☐ not started';
  return (
    <div
      className={`rounded-xl border p-4 ${c.bg} ${c.border} ${isCurrent ? 'ring-2 ring-amber-300' : ''}`}
    >
      <div className="flex items-baseline justify-between">
        <p className={`text-xs font-semibold tracking-wider uppercase ${c.text}`}>{stage.label}</p>
        <p className="text-xs text-gray-500">{status}</p>
      </div>
      <p className={`mt-2 text-2xl font-bold ${c.text}`}>{pct}%</p>
      <p className="text-xs text-gray-500">
        {stage.done.toLocaleString()} / {stage.total.toLocaleString()}
      </p>
      <div className={`mt-2 h-1.5 overflow-hidden rounded-full ${c.barBg}`}>
        <div
          className={`h-full ${c.bar} transition-all duration-500`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
      <p className="mt-2 text-[11px] text-gray-500">{stage.caption}</p>
    </div>
  );
}

/* ── Discrepancies: items still needing a PM decision (matches Reconciliation page's "open" buckets) ── */
function Discrepancies({ d }) {
  const items = [
    { label: 'Not found on site — proposed write-off', count: d.missing, color: 'red' },
    { label: 'New finds awaiting confirmation', count: d.new_finds, color: 'sky' },
    { label: 'Awaiting recheck', count: d.recheck, color: 'amber' },
    { label: 'Conflicted', count: d.conflicted, color: 'red' },
  ].filter((it) => it.count > 0);
  const palette = {
    amber: 'text-amber-700 bg-amber-50 border-amber-200',
    red: 'text-red-700 bg-red-50 border-red-200',
    purple: 'text-purple-700 bg-purple-50 border-purple-200',
    sky: 'text-sky-700 bg-sky-50 border-sky-200',
  };
  return (
    <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5">
      <p className="mb-3 text-sm font-semibold tracking-wider text-gray-500 uppercase">
        Open discrepancies to discuss with client ({d.total.toLocaleString()})
      </p>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it.label} className="flex items-center justify-between gap-4">
            <span className="text-sm text-gray-700">{it.label}</span>
            <span
              className={`inline-flex min-w-[56px] justify-center rounded-md border px-2 py-0.5 text-sm font-bold ${palette[it.color]}`}
            >
              {it.count.toLocaleString()}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Stripe: a single horizontal bar segmented by physical state ── */
function Stripe({ phys, universe }) {
  const segments = [
    { count: phys.tagged, color: 'bg-emerald-500', label: 'Tagged' },
    { count: phys.untaggable, color: 'bg-purple-500', label: 'Untaggable' },
    { count: phys.retired, color: 'bg-orange-500', label: 'Retired' },
    { count: phys.missing, color: 'bg-red-500', label: 'Missing' },
    { count: phys.pending, color: 'bg-amber-400', label: 'Pending' },
  ].filter((s) => s.count > 0);

  return (
    <div className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100">
      {segments.map((s) => (
        <div
          key={s.label}
          className={`${s.color} h-full transition-all duration-500`}
          style={{ width: `${(s.count / universe) * 100}%` }}
          title={`${s.label}: ${s.count.toLocaleString()}`}
        />
      ))}
    </div>
  );
}

/* ── State chip — one bucket from the stripe ─ */
function StateChip({ label, count, color, sub }) {
  const palette = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    purple: 'border-purple-200 bg-purple-50 text-purple-700',
    orange: 'border-orange-200 bg-orange-50 text-orange-700',
    red: 'border-red-200 bg-red-50 text-red-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    gray: 'border-gray-200 bg-gray-50 text-gray-500',
  };
  const c = palette[color] || palette.gray;
  return (
    <div className={`rounded-lg border px-3 py-2 ${c}`}>
      <p className="text-[10px] font-medium tracking-wider uppercase opacity-80">{label}</p>
      <p className="text-xl font-bold">{count.toLocaleString()}</p>
      {sub && <p className="text-[10px] opacity-70">{sub}</p>}
    </div>
  );
}

/* ── Deliverable readiness — gates as a checklist ── */
function DeliverableReadiness({ phys, stage }) {
  const items = [
    {
      label: `All ${phys.universe.toLocaleString()} FAR assets resolved`,
      done: phys.pending === 0,
      detail: phys.pending > 0 ? `${phys.pending} still pending` : 'all states final',
    },
    {
      label: 'All discoveries QA-approved',
      done: true,
      detail: `${phys.new_finds.toLocaleString()} approved`,
    },
    {
      label: 'NT defensibility memo signed',
      done: false,
      detail:
        phys.untaggable > 0 ? `${phys.untaggable} untaggable items to document` : 'no NT items',
    },
    {
      label: 'Reconciliation closeout signed',
      done: stage === 'final_delivery',
      detail: stage === 'final_delivery' ? 'closed' : 'pending PM signoff',
    },
  ];

  return (
    <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5">
      <p className="mb-3 text-sm font-semibold tracking-wider text-gray-500 uppercase">
        Ready for client when…
      </p>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it.label} className="flex items-start gap-3">
            <span
              className={`mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${
                it.done ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-300'
              }`}
            >
              {it.done ? (
                <svg
                  className="h-3 w-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
              )}
            </span>
            <div className="min-w-0">
              <p className={`text-sm ${it.done ? 'text-gray-700' : 'text-gray-900'}`}>{it.label}</p>
              <p className="text-xs text-gray-400">{it.detail}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Building progress (kept — actionable) ─ */
function BuildingProgress({ buildings, sort, onSort }) {
  if (!buildings.length) return null;
  const sorted = [...buildings].sort((a, b) => {
    if (sort === 'pct') return a.pct - b.pct;
    if (sort === 'remaining') return b.total - b.collected - (a.total - a.collected);
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
        <h3 className="text-sm font-semibold tracking-wider text-gray-700 uppercase">
          Building progress
        </h3>
        <div className="flex gap-1">
          {[
            { key: 'remaining', label: 'Remaining' },
            { key: 'pct', label: '% Tagged' },
            { key: 'name', label: 'Name' },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => onSort(opt.key)}
              className={`rounded px-2.5 py-1 text-[11px] font-medium transition-colors ${
                sort === opt.key ? 'bg-gray-200 text-gray-800' : 'text-gray-400 hover:text-gray-900'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div className="divide-y divide-gray-200">
        {sorted.map((b) => {
          const remaining = b.total - b.collected;
          const barColor =
            b.pct >= 90 ? 'bg-emerald-500' : b.pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
          const textColor =
            b.pct >= 90 ? 'text-emerald-600' : b.pct >= 50 ? 'text-amber-600' : 'text-red-600';
          return (
            <div
              key={b.id}
              className="flex items-center gap-4 px-5 py-3 transition-colors hover:bg-gray-50"
            >
              <div className="w-40 flex-shrink-0">
                <p className="truncate text-sm font-medium text-gray-800">{b.name}</p>
              </div>
              <div className="flex-1">
                <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className={`h-full ${barColor} rounded-full transition-all duration-500`}
                    style={{ width: `${b.pct}%` }}
                  />
                </div>
              </div>
              <div className="w-28 flex-shrink-0 text-right">
                <span className="text-xs text-gray-500">
                  {b.collected.toLocaleString()}/{b.total.toLocaleString()}
                </span>
              </div>
              <div className="w-14 flex-shrink-0 text-right">
                <span className={`text-sm font-bold ${textColor}`}>{b.pct}%</span>
              </div>
              {remaining > 0 && (
                <div className="w-20 flex-shrink-0 text-right">
                  <span className="text-[10px] text-gray-400">{remaining} left</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Skeleton ── */
function Skeleton() {
  return (
    <div className="animate-pulse space-y-6">
      <div className="h-6 w-48 rounded bg-gray-100" />
      <div className="h-28 rounded-xl bg-gray-100" />
      <div className="h-32 rounded-xl bg-gray-100" />
      <div className="h-40 rounded-xl bg-gray-100" />
      <div className="h-64 rounded-xl bg-gray-100" />
    </div>
  );
}
